import React, { useCallback, useEffect, useState } from 'react'
import { CBadge, CCard, CCardBody, CFormSelect, CSmartTable } from '@coreui/react-pro'
import moment from 'moment'

import ToastComponent from 'src/components/common/TaostComponent.js'
import TableSearchToolbar from 'src/components/common/TableSearchToolbar'
import UserService from 'src/service/UserService'
import './User.css'

const SEARCH_FIELDS = [
  { key: 'full_name', label: 'Member' },
  { key: 'email', label: 'Email' },
  { key: 'phone', label: 'Phone' },
  { key: 'id', label: 'Member ID' },
]

const STATUS_OPTIONS = [
  { value: '', label: 'All statuses' },
  { value: '1', label: 'Activated' },
  { value: '0', label: 'Deactivated' },
]

const columns = [
  { label: 'Member ID', key: 'id', filter: false, sorter: true, _style: { width: '10%' } },
  { label: 'Member', key: 'full_name', filter: false, sorter: true },
  { label: 'Email', key: 'email', filter: false, sorter: true },
  { label: 'Phone', key: 'phone', filter: false, sorter: false },
  { label: 'Registered', key: 'created_at', filter: false, sorter: true },
  { label: 'Status', key: 'is_active', filter: false, sorter: true },
]

const GameUser = () => {
  const [loading, setLoading] = useState(false)
  const [activePage, setActivePage] = useState(1)
  const [columnFilter, setColumnFilter] = useState({})
  const [searchField, setSearchField] = useState('full_name')
  const [columnSorter, setColumnSorter] = useState(null)
  const [itemsPerPage, setItemsPerPage] = useState(10)
  const [users, setUsers] = useState()

  const clearSearchKeys = (filter) => {
    const next = { ...filter }
    SEARCH_FIELDS.forEach((f) => delete next[f.key])
    return next
  }

  const applySearch = (value) => {
    setActivePage(1)
    const next = clearSearchKeys(columnFilter)
    if (value) next[searchField] = value
    setColumnFilter(next)
  }

  const handleFieldChange = (key) => {
    setSearchField(key)
    setColumnFilter((prev) => clearSearchKeys(prev))
  }

  const handleStatusFilter = (value) => {
    setActivePage(1)
    setColumnFilter((prev) => {
      const next = { ...prev }
      if (value === '') delete next.is_active
      else next.is_active = value
      return next
    })
  }

  const fetchData = useCallback(() => {
    const offset = itemsPerPage * activePage - itemsPerPage
    const params = new URLSearchParams()
    Object.keys(columnFilter).forEach((key) => {
      if (columnFilter[key] !== '' && columnFilter[key] !== undefined) {
        params.append(key, columnFilter[key])
      }
    })
    if (columnSorter && columnSorter.column !== undefined) {
      params.append('sort', `${columnSorter.column}%${columnSorter.state}`)
    }

    setLoading(true)
    UserService.getAdminUserListing(offset, itemsPerPage, activePage, params, false, true)
      .then((result) => {
        setUsers(result.data)
      })
      .catch((e) => {
        ToastComponent(e?.response?.data?.message || 'Could not load members', 'error')
      })
      .finally(() => setLoading(false))
  }, [activePage, columnFilter, columnSorter, itemsPerPage])

  useEffect(() => {
    fetchData()
  }, [fetchData])

  const activeSearchLabel = SEARCH_FIELDS.find((f) => f.key === searchField)?.label

  return (
    <>
      <div className="ge-page-header">
        <h1 className="ge-page-title">Users</h1>
        <p className="ge-page-subtitle">
          Every registered member. Click a member&apos;s name to log in as them on the front end.
        </p>
      </div>

      <CCard className="um-panel">
        <CCardBody>
          <div className="um-panel-heading">
            <div>
              <h2 className="um-panel-title">
                Manage Members -
                {users?.total !== undefined && <span className="ge-tab-count">{users.total}</span>}
              </h2>
              <p className="um-panel-note">Search members across all games.</p>
            </div>
          </div>

          <TableSearchToolbar
            fields={SEARCH_FIELDS}
            field={searchField}
            onFieldChange={handleFieldChange}
            value={columnFilter?.[searchField] || ''}
            onSearch={applySearch}
            placeholder={`Search by ${activeSearchLabel}...`}
            trailing={
              <CFormSelect
                size="sm"
                aria-label="Filter by status"
                value={columnFilter?.is_active ?? ''}
                onChange={(e) => handleStatusFilter(e.target.value)}
              >
                {STATUS_OPTIONS.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </CFormSelect>
            }
          />

          <div className="ge-datatable ge-datatable--no-select ge-datatable--static">
            <CSmartTable
              columns={columns}
              columnSorter={{ external: true }}
              noItemsLabel="No members found"
              scopedColumns={{
                id: (item) => <td className="um-muted">{item.id}</td>,
                full_name: (item) => (
                  <td>
                    <a
                      href={`${process.env.REACT_APP_FRONT_URL}adminLogin/${item.id}`}
                      target="_blank"
                      rel="noreferrer"
                      className="fw-semibold text-primary text-decoration-none"
                      title={`Login as ${item.full_name || 'this member'}`}
                    >
                      {item.full_name || '-'}
                    </a>
                  </td>
                ),
                email: (item) => <td>{item.email || '-'}</td>,
                phone: (item) => <td>{item.phone || '-'}</td>,
                created_at: (item) => (
                  <td>{item.created_at ? moment(item.created_at).format('D.MM.YYYY') : '-'}</td>
                ),
                is_active: (item) => (
                  <td>
                    <CBadge
                      color={item.is_active === 1 ? 'success' : 'warning'}
                      shape="rounded-pill"
                      className="px-2 py-1"
                    >
                      {item.is_active === 1 ? 'Activated' : 'Deactivated'}
                    </CBadge>
                  </td>
                ),
              }}
              items={users?.data}
              itemsPerPage={itemsPerPage}
              itemsPerPageSelect
              loading={loading}
              pagination={{ external: true }}
              paginationProps={{
                activePage,
                pages: Math.ceil(users?.total / itemsPerPage) || 1,
              }}
              tableProps={{ hover: true, responsive: true, className: 'align-middle mb-0' }}
              onActivePageChange={(page) => setActivePage(page)}
              onItemsPerPageChange={(perPage) => {
                setActivePage(1)
                setItemsPerPage(perPage)
              }}
              onSorterChange={(sorter) => setColumnSorter(sorter)}
            />
          </div>
        </CCardBody>
      </CCard>
    </>
  )
}

export default GameUser
